/**
 * ===========================================================================
 *  PROGRESS OVERLAY — the number the player watches climb
 * ===========================================================================
 *
 * DEV_PLAN Step 2's legibility half. `RUBRIC.md` scores
 * `progressionLegibility` on two things: a number or bar that visibly MOVES
 * during a battle, and a named next unlock. This draws the first.
 *
 * Same arrangement as `missionhud.js`, and for the same reason: `src/ui/*` is
 * the HUD's to own (docs/CONTRACTS.md §4), so the game layer mounts its own
 * element inside the published `world.ui.root` and styles it with the HUD's
 * grammar — the `--u` scale unit, gold rules, letter-spaced uppercase labels,
 * cut corners. It inherits `--u`, so it scales with everything else.
 *
 * The HUD's house rules apply here too: no `@keyframes` (Playwright screenshots
 * with animations disabled, so an animated element photographs mid-pose), no
 * external fonts, and no per-frame allocation — every DOM write goes through a
 * last-value cache, so a still frame writes nothing.
 *
 * WHY "STANDING" AND NOT "RANK". The results screen already has a Rank, and it
 * is a different quantity: `src/ui/hud.js` grades each battle S/A/B/C/D and
 * `menus.js` prints it under that word. Putting a career rank beside it under
 * the same label put two unrelated numbers on one frame calling themselves the
 * same thing — visible in shots/prgui/001_ui_results.png as "B RANK Seasoned"
 * next to "RANK 1 > 6 LURAH". The battle grade owns the word throughout the
 * codebase, so the newcomer is the one that moves.
 *
 * Placement: directly above the player plate, left-aligned with it and the
 * same width as its column, so it reads as part of the player's own block
 * rather than as a fifth floating widget. The plate occupies bottom 26u to
 * about 104u; this sits at 116u, clear of it and clear of the centred capture
 * meter at 46u.
 */

const CSS = `
.sb .prg{position:absolute; left:calc(var(--u)*30); bottom:calc(var(--u)*116);
  width:calc(var(--u)*306); opacity:0}
.sb .prg .pgrow{display:flex; align-items:baseline; gap:calc(var(--u)*8)}
.sb .prg .pgk{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3.2);
  text-transform:uppercase; color:rgba(226,190,124,.62)}
.sb .prg .pgn{font-size:calc(var(--u)*19); font-weight:400; letter-spacing:calc(var(--u)*1.4);
  color:#fbf1dc; line-height:1;
  text-shadow:0 calc(var(--u)*2) calc(var(--u)*8) rgba(0,0,0,.9), 0 0 calc(var(--u)*16) rgba(226,168,72,.28)}
.sb .prg .pgt{margin-left:auto; font-size:calc(var(--u)*10.5); letter-spacing:calc(var(--u)*1.8);
  text-transform:uppercase; font-weight:700; color:rgba(232,196,140,.74)}

.sb .prg .pgw{display:flex; align-items:baseline; gap:calc(var(--u)*8);
  margin-top:calc(var(--u)*4)}
.sb .prg .pgwn{font-size:calc(var(--u)*9.5); letter-spacing:calc(var(--u)*1.6);
  text-transform:uppercase; font-weight:700; color:rgba(238,226,200,.62)}
.sb .prg .pglv{margin-left:auto; font-size:calc(var(--u)*9.5); font-weight:700;
  letter-spacing:calc(var(--u)*1.6); color:rgba(226,190,124,.82)}

/* The bar. One filled child scaled on X — never a width, which would relayout. */
.sb .prg .pgbar{position:relative; height:calc(var(--u)*7); margin-top:calc(var(--u)*5);
  background:rgba(10,7,5,.78);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.34)}
.sb .prg .pgbar>i{position:absolute; inset:0; transform-origin:0 50%; transform:scaleX(0);
  background:linear-gradient(90deg, rgba(206,156,64,.96), rgba(248,216,142,1));
  box-shadow:0 0 calc(var(--u)*7) rgba(255,198,104,.45)}
.sb .prg .pgbar>b{position:absolute; top:calc(var(--u)*-2); left:0; width:calc(var(--u)*1.5);
  height:calc(var(--u)*11); background:#fff3d6; box-shadow:0 0 calc(var(--u)*8) rgba(255,214,128,.85)}

/* A level or rank just landed. A static class, not an animation: the frame a
   screenshot catches must look the same as the frame a player sees. */
.sb .prg.up .pgn{color:#fff6e2; text-shadow:0 0 calc(var(--u)*22) rgba(255,206,120,.95),
  0 calc(var(--u)*2) calc(var(--u)*8) rgba(0,0,0,.9)}
.sb .prg.up .pgbar>i{background:linear-gradient(90deg, rgba(240,206,132,.96), #fff3d6)}

/* ---------------------------------------------------------------- the band
   Shown on the two screens that bracket a battle: the briefing, where it names
   what the next rung is called, and the results, where it shows what the
   battle just paid. Anchored to the bottom of the viewport rather than placed
   inside the results modal, because that modal is src/ui/menus.js's markup
   and this module does not own it. The modal body is vertically centred and
   ends at its button row, which leaves this clear — verified on a frame, not
   assumed from the CSS. */
.sb .prgb{position:absolute; left:50%; bottom:calc(var(--u)*22);
  width:calc(var(--u)*620); max-width:92%; margin-left:calc(var(--u)*-310);
  opacity:0; pointer-events:none}
.sb .prgb .bwrap{padding:calc(var(--u)*9) calc(var(--u)*16) calc(var(--u)*10);
  background:linear-gradient(180deg, rgba(14,10,8,.72), rgba(6,5,4,.46));
  clip-path:polygon(0 0, 100% 0, 100% calc(100% - var(--u)*11),
                    calc(100% - var(--u)*11) 100%, 0 100%);
  box-shadow:inset calc(var(--u)*2) 0 0 rgba(228,190,116,.55),
             0 calc(var(--u)*3) calc(var(--u)*12) rgba(0,0,0,.55)}
.sb .prgb .brow{display:flex; align-items:baseline; gap:calc(var(--u)*10)}
.sb .prgb .bk{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3.2);
  text-transform:uppercase; color:rgba(226,190,124,.62)}
.sb .prgb .bnum{font-size:calc(var(--u)*24); line-height:1; color:#ffeaba;
  text-shadow:0 calc(var(--u)*3) calc(var(--u)*14) rgba(0,0,0,.9), 0 0 calc(var(--u)*26) rgba(255,180,60,.45)}
.sb .prgb .btitle{font-size:calc(var(--u)*12); font-weight:700; letter-spacing:calc(var(--u)*3.4);
  text-transform:uppercase; color:#f4e4c2}
.sb .prgb .bxp{margin-left:auto; font-size:calc(var(--u)*9.5); font-weight:700;
  letter-spacing:calc(var(--u)*1.8); text-transform:uppercase; color:rgba(226,190,124,.72)}
.sb .prgb .brow2{display:flex; align-items:baseline; gap:calc(var(--u)*10);
  margin-top:calc(var(--u)*6)}
.sb .prgb .bw{font-size:calc(var(--u)*9.5); font-weight:700; letter-spacing:calc(var(--u)*1.8);
  text-transform:uppercase; color:rgba(238,226,200,.66)}
.sb .prgb .brow3{display:flex; align-items:baseline; gap:calc(var(--u)*10);
  margin-top:calc(var(--u)*7)}
.sb .prgb .bbrv{font-size:calc(var(--u)*9); letter-spacing:calc(var(--u)*1.3);
  color:rgba(226,186,124,.72)}
.sb .prgb .bgrant{margin-left:auto; font-size:calc(var(--u)*9.5); font-weight:700;
  letter-spacing:calc(var(--u)*1.6); color:#ffe6ac;
  text-shadow:0 0 calc(var(--u)*14) rgba(255,170,60,.45)}
.sb .prgb .bnext{margin-left:auto; font-size:calc(var(--u)*9.5); letter-spacing:calc(var(--u)*1.6);
  color:rgba(232,196,140,.78)}
.sb .prgb .bbar{position:relative; height:calc(var(--u)*6); margin-top:calc(var(--u)*5);
  background:rgba(12,9,7,.66);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.24)}
.sb .prgb .bbar>i{position:absolute; inset:0; transform-origin:0 50%; transform:scaleX(0);
  background:linear-gradient(90deg, rgba(196,148,62,.92), rgba(240,206,132,.96))}
/* where the bar stood when the battle began, so the gain is visible as a gap */
.sb .prgb .bbar>u{position:absolute; top:0; bottom:0; width:calc(var(--u)*1.5);
  background:rgba(255,243,214,.55); text-decoration:none}
`;

function tx(el, s) { if (el.__t !== s) { el.__t = s; el.textContent = s; } }
function op(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__o !== undefined && Math.abs(el.__o - v) < 0.004) return;
  el.__o = v; el.style.opacity = v.toFixed(3);
}
function sx(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__s !== undefined && Math.abs(el.__s - v) < 0.0015) return;
  el.__s = v; el.style.transform = 'scaleX(' + v.toFixed(4) + ')';
}

export function createProgressHud() {
  let mounted = false;
  let root = null, styleEl = null, band = null;
  const R = {}, B = {};
  const S = { show: 0, bshow: 0, frac: 0, rank: -1, lvl: -1, up: 0, upOn: false };

  function mount(w) {
    if (mounted) return true;
    const host = w.ui && w.ui.root;
    if (!host || !host.appendChild) return false;

    styleEl = document.createElement('style');
    styleEl.textContent = CSS;
    host.appendChild(styleEl);

    root = document.createElement('div');
    root.className = 'prg';
    root.innerHTML = `<div class="pgrow">
        <span class="pgk">Standing</span><span class="pgn ser">1</span><span class="pgt"></span>
      </div>
      <div class="pgw"><span class="pgwn"></span><span class="pglv">Lv 0</span></div>
      <div class="pgbar"><i></i><b></b></div>`;
    host.appendChild(root);

    R.num = root.querySelector('.pgn');
    R.title = root.querySelector('.pgt');
    R.weapon = root.querySelector('.pgwn');
    R.lv = root.querySelector('.pglv');
    R.fill = root.querySelector('.pgbar>i');
    R.edge = root.querySelector('.pgbar>b');

    band = document.createElement('div');
    band.className = 'prgb';
    band.innerHTML = `<div class="bwrap">
        <div class="brow"><span class="bk">Standing</span><span class="bnum ser">1</span>
          <span class="btitle"></span><span class="bxp"></span></div>
        <div class="brow2"><span class="bw"></span><span class="bnext"></span></div>
        <div class="bbar"><i></i><u></u></div>
        <div class="brow3"><span class="bbrv"></span><span class="bgrant"></span></div>
      </div>`;
    host.appendChild(band);

    B.num = band.querySelector('.bnum');
    B.title = band.querySelector('.btitle');
    B.xp = band.querySelector('.bxp');
    B.weapon = band.querySelector('.bw');
    B.next = band.querySelector('.bnext');
    B.fill = band.querySelector('.bbar>i');
    B.mark = band.querySelector('.bbar>u');
    B.brv = band.querySelector('.bbrv');
    B.grant = band.querySelector('.bgrant');

    mounted = true;
    return true;
  }

  /**
   * The between-battles band. `info` is `world.progress.info()`; `startFrac`
   * is where this weapon's bar stood when the battle began, drawn as a tick so
   * the gain reads as a distance rather than as a number to be trusted.
   */
  function bandFrame(w, realDt, mode, info, startFrac) {
    if (!mounted) return;
    const k = Math.min(0.1, realDt > 0 ? realDt : 1 / 60);
    const visible = mode === 'results' || mode === 'briefing';
    S.bshow += ((visible && info ? 1 : 0) - S.bshow) * Math.min(1, k * 7);
    op(band, S.bshow);
    if (!info || S.bshow < 0.004) return;

    /* The two screens ask different questions and must not be given the same
     * answer. `results` looks BACK: what did this battle pay, and how far did
     * the bar travel. `briefing` looks FORWARD: where do I stand, what is
     * next. Showing "+953 XP this battle" on the briefing for a battle that
     * has not started would be a lie about the battle in front of the player
     * -- and it is what this did until the stale `earned` was noticed, because
     * a record is only reloaded when a mission STARTS. */
    const report = mode === 'results';
    const climbed = report && info.rank > info.rankAtStart;
    tx(B.num, climbed ? info.rankAtStart + ' \u25b8 ' + info.rank : String(info.rank));
    tx(B.title, info.title ? info.title.name : '');
    tx(B.xp, (report && info.earnedXp > 0) ? '+' + info.earnedXp.toLocaleString() + ' XP this battle' : '');
    tx(B.weapon, (info.weaponLabel || '') + '  \u00b7  Lv ' + info.level);
    /* PREFER THE GRANT OVER THE TITLE.
     *
     * "Next: Tumenggung at Rank 9" names a word. "Next: Gada Rujakpala at
     * Standing 4" names a weapon the player cannot currently pick up, which is
     * the thing the row `reasonToReplay` is actually asking about — it was
     * held at 4 across three rounds because nothing the ladder granted changed
     * what the player could DO. The title is still shown when there is no
     * grant left ahead. */
    const ng = w.progress && w.progress.nextGrant && w.progress.nextGrant();
    tx(B.next, ng
      ? 'Next: ' + ng.name + ' at Standing ' + ng.rank + ' \u2014 ' + ng.note
      : info.next
        ? 'Next: ' + info.next.name + ' at Standing ' + info.next.rank + ' \u2014 ' + info.next.note
        : 'The ladder is climbed');

    /* WHAT THE BRAVERY BOUGHT. Recorded as open since Step 3: "Bravery is not
     * surfaced anywhere but the HUD pips: nothing on the results or briefing
     * screens says what it is or what it bought." A resource the player only
     * ever sees as five dots during a fight is one they never reason about
     * between fights. */
    const P = w.player;
    if (report && P) {
      const bits = [];
      if (P.braveryEarned > 0) bits.push(P.braveryEarned.toFixed(1) + ' Bravery earned');
      if (P.braverySpent > 0) bits.push(P.braverySpent.toFixed(0) + ' spent');
      if (P.parries > 0) bits.push(P.parries + ' ' + (P.parries === 1 ? 'parry' : 'parries'));
      if (P.perfectDodges > 0) bits.push(P.perfectDodges + ' perfect ' + (P.perfectDodges === 1 ? 'dodge' : 'dodges'));
      if (P.braveryDenied > 0) bits.push(P.braveryDenied + ' refused for want of it');
      tx(B.brv, bits.join('  \u00b7  '));
    } else {
      tx(B.brv, report ? '' : 'Bravery pays for the charge branches — a ring costs three, a stun one');
    }

    /* ...and anything the ladder handed over on the way. */
    const got = (w.progress && w.progress.granted && w.progress.granted()) || [];
    const fresh = report ? got.filter((g) => g.rank > info.rankAtStart && g.rank <= info.rank) : [];
    tx(B.grant, fresh.length
      ? 'Unlocked: ' + fresh.map((g) => g.name).join(', ')
      : (report ? '' : (got.length ? got.length + ' weapon(s) earned' : '')));
    sx(B.fill, info.levelFrac);
    // the start tick marks a distance travelled, so it means nothing on a
    // screen that is looking forward, and nothing when it is not behind the fill
    const sf = (report && startFrac >= 0 && startFrac < info.levelFrac) ? startFrac : -1;
    if (B.mark.__x !== sf) {
      B.mark.__x = sf;
      B.mark.style.display = sf < 0 ? 'none' : 'block';
      if (sf >= 0) B.mark.style.left = (sf * 100).toFixed(2) + '%';
    }
  }

  /**
   * @param info  `world.progress.info()`, or null to just fade out.
   * @param visible  battle in progress and no modal over it.
   */
  function frame(w, realDt, visible, info) {
    if (!mounted) return;
    const k = Math.min(0.1, realDt > 0 ? realDt : 1 / 60);
    S.show += ((visible && info ? 1 : 0) - S.show) * Math.min(1, k * 6);
    op(root, S.show);
    if (!info || S.show < 0.004) return;

    if (info.rank !== S.rank || info.level !== S.lvl) {
      // Only a climb is worth celebrating; a reload back to rank 1 is not.
      if (S.rank >= 0 && (info.rank > S.rank || info.level > S.lvl)) {
        S.up = 1.6;
        if (info.level > S.lvl && S.lvl >= 0) {
          w.ui?.toast?.(`${info.weaponLabel} — Level ${info.level}`);
        }
        if (info.rank > S.rank && S.rank >= 0) {
          w.ui?.toast?.(`Standing ${info.rank} — ${info.title ? info.title.name : ''}`);
        }
      }
      S.rank = info.rank; S.lvl = info.level;
      tx(R.num, String(info.rank));
      tx(R.title, info.title ? info.title.name : '');
      tx(R.lv, 'Lv ' + info.level);
    }
    tx(R.weapon, info.weaponLabel || '');

    if (S.up > 0) {
      S.up -= k;
      if (S.up <= 0) S.up = 0;
    }
    const on = S.up > 0;
    if (on !== S.upOn) { S.upOn = on; root.classList.toggle('up', on); }

    // Ease toward the true fraction so a single kill reads as movement rather
    // than as a jump, and so the bar is never mid-jump in a screenshot.
    S.frac += (info.levelFrac - S.frac) * Math.min(1, k * 9);
    // a level-up wraps the fraction; snap rather than run the bar backwards
    if (info.levelFrac < S.frac - 0.5) S.frac = info.levelFrac;
    sx(R.fill, S.frac);
    const ex = S.frac * 100;
    if (R.edge.__x === undefined || Math.abs(R.edge.__x - ex) > 0.12) {
      R.edge.__x = ex;
      R.edge.style.left = ex.toFixed(2) + '%';
    }
  }

  function clear() {
    if (!mounted) return;
    S.frac = 0; S.rank = -1; S.lvl = -1; S.up = 0;
  }

  function dispose() {
    root && root.remove(); band && band.remove(); styleEl && styleEl.remove();
    mounted = false;
  }

  return { mount, frame, bandFrame, clear, dispose, get mounted() { return mounted; },
    get el() { return root; }, get bandEl() { return band; } };
}
